// RIBBON priority-collapse — the pure decision function (R0, 2026-07-20).
//
// No React, no DOM, no live-editor imports: (available width + measured group
// widths + priorities) → (visible set + collapsed set). RibbonRow measures and
// re-renders; THIS module decides. Deterministic so the behavior is unit-
// testable at any width without a browser (collapsePriority.unit.mjs).
//
// The model (CONCEPT-text-ribbon §3): groups collapse into their labeled "…"
// chips by an explicit ORDER OF SACRIFICE. A group is one of:
//   • neverCollapse: true       — never sacrificed (scope, font, size, color,
//                                 B/I/U on the text row);
//   • collapsePriority: n       — numbered sacrifice, ascending (1 = first
//                                 to fold; the text row's Fit→Spacing→Curve→
//                                 Lists→Case is 1..5);
//   • neither                   — unnumbered: sacrificed only after every
//                                 numbered group, right-to-left (Word-style).
//
// Collapsing a group swaps its full width for its chip width IN PLACE (chips
// hold their slot — the row's slot count and gap count never change, which is
// what keeps the loop math linear and the layout stable).
//
// FOLDING-MUST-HELP guard: a group whose chip measures at least as wide as
// the group itself is SKIPPED (folding it would grow the row). In practice
// this exempts one-small-button groups like text Fit — the §3 order remains
// the law among groups whose chips actually save space. Probes therefore
// assert against the EFFECTIVE order (order filtered by this guard).

/** Sacrifice order for a rowset's groups (ids, first-to-fold first).
 * @param {Array<{id:string, collapsePriority?:number, neverCollapse?:boolean}>} groups
 *   in VISUAL order (left → right).
 * @returns {string[]} ids: numbered ascending (ties fold right-to-left),
 *   then unnumbered right-to-left. neverCollapse groups are absent.
 */
export function buildSacrificeOrder(groups) {
  const numbered = groups
    .filter((g) => !g.neverCollapse && Number.isFinite(g.collapsePriority))
    .sort((a, b) => (a.collapsePriority - b.collapsePriority)
      || (groups.indexOf(b) - groups.indexOf(a)));
  const unnumbered = groups
    .filter((g) => !g.neverCollapse && !Number.isFinite(g.collapsePriority))
    .reverse();
  return [...numbered, ...unnumbered].map((g) => g.id);
}

/** Decide which groups fold at a given width.
 * @param {object} p
 * @param {Array<{id:string, width:number, chipWidth?:number,
 *   collapsePriority?:number, neverCollapse?:boolean}>} p.groups in visual
 *   order; width = measured natural (expanded) width, chipWidth = measured
 *   collapsed-chip width (fallback applies until measured).
 * @param {number} p.availableWidth  the row's content width in px.
 * @param {number} [p.gap=8]         the row's CSS gap (slot count is constant,
 *   so gaps contribute (n−1)·gap regardless of what is collapsed).
 * @param {number} [p.chipWidthFallback=72]
 * @returns {{visible:string[], collapsed:string[], fits:boolean,
 *   usedWidth:number}} collapsed is in sacrifice order (a PREFIX of
 *   buildSacrificeOrder — the probe asserts exactly this); fits=false means
 *   even the fully-folded row is wider than availableWidth (never-collapse
 *   floor reached).
 */
export function computeCollapse({ groups, availableWidth, gap = 8, chipWidthFallback = 72 }) {
  const chipW = (g) => (Number.isFinite(g.chipWidth) ? g.chipWidth : chipWidthFallback);
  const fullW = (g) => (Number.isFinite(g.width) ? g.width : 0);
  const gaps = gap * Math.max(0, groups.length - 1);
  const collapsedSet = new Set();
  const total = () => groups.reduce(
    (sum, g) => sum + (collapsedSet.has(g.id) ? chipW(g) : fullW(g)),
    gaps,
  );

  let used = total();
  const collapsed = [];
  if (used > availableWidth) {
    for (const id of buildSacrificeOrder(groups)) {
      const g = groups.find((x) => x.id === id);
      // Folding only helps if the chip is actually narrower than the group
      // (a one-button group can be slimmer than its labeled chip).
      if (chipW(g) >= fullW(g)) continue;
      collapsedSet.add(id);
      collapsed.push(id);
      used = total();
      if (used <= availableWidth) break;
    }
  }

  return {
    visible: groups.filter((g) => !collapsedSet.has(g.id)).map((g) => g.id),
    collapsed,
    fits: used <= availableWidth,
    usedWidth: used,
  };
}

/** Shared row geometry — single source for the JS math AND the rendered gap
 * (RibbonRow sets style.gap from this so CSS can never drift from the
 * algorithm's assumption). */
export const ROW_GAP = 8;
